import React, { useMemo, useState } from "react";
import {
  Box, Card, CardContent, CircularProgress, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, TextField, InputAdornment, Stack,
  MenuItem, Select, TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { OrderVM, OrderStatus } from "../data/types";

export interface OrderCol {
  header: string;
  align?: "left" | "right";
  render: (o: OrderVM) => React.ReactNode;
}

interface Props {
  title: string;
  rows: OrderVM[];
  loading: boolean;
  columns: OrderCol[];
  onOpen: (o: OrderVM) => void;
  empty?: string;
}

const STATUSES: (OrderStatus | "All")[] =
  ["All", "New", "Pending", "Confirmed", "Preparing", "Delivered", "Cancelled"];

/**
 * Shared orders list (Oct-10 QA #2): search, status + date-range filtering,
 * sorting and pagination, used by both the Buyer and Distributor order lists.
 */
const OrdersListTable: React.FC<Props> = ({ title, rows, loading, columns, onOpen, empty }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<OrderStatus | "All">("All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState("dateDesc");
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = rows.filter((o) => {
      if (status !== "All" && o.status !== status) return false;
      if (s && !(o.orderId.toLowerCase().includes(s)
        || (o.customer || "").toLowerCase().includes(s)
        || o.status.toLowerCase().includes(s))) return false;
      if (from && o.orderDate && dayjs(o.orderDate).isBefore(dayjs(from), "day")) return false;
      if (to && o.orderDate && dayjs(o.orderDate).isAfter(dayjs(to), "day")) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      switch (sort) {
        case "dateAsc":  return dayjs(a.orderDate || 0).valueOf() - dayjs(b.orderDate || 0).valueOf();
        case "totalDesc": return b.total - a.total;
        case "totalAsc":  return a.total - b.total;
        default:          return dayjs(b.orderDate || 0).valueOf() - dayjs(a.orderDate || 0).valueOf();
      }
    });
    return list;
  }, [rows, q, status, from, to, sort]);

  // Keep the current page in range as filters change.
  const pageSafe = Math.min(page, Math.max(0, Math.ceil(filtered.length / rpp) - 1));
  const paged = filtered.slice(pageSafe * rpp, pageSafe * rpp + rpp);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filtered.length} of {rows.length} order{rows.length === 1 ? "" : "s"}
      </Typography>

      <Card>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <TextField
              size="small" placeholder="Search order #, customer or status"
              value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
              sx={{ minWidth: { xs: "100%", sm: 260 }, flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Select size="small" value={status} onChange={(e) => { setStatus(e.target.value as any); setPage(0); }} sx={{ minWidth: 150 }}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s === "All" ? "All statuses" : s}</MenuItem>)}
            </Select>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} sx={{ minWidth: 150 }} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} sx={{ minWidth: 150 }} />
            <Select size="small" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 170 }}>
              <MenuItem value="dateDesc">Newest first</MenuItem>
              <MenuItem value="dateAsc">Oldest first</MenuItem>
              <MenuItem value="totalDesc">Total: high to low</MenuItem>
              <MenuItem value="totalAsc">Total: low to high</MenuItem>
            </Select>
          </Stack>

          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              {empty || "No orders match your filters."}
            </Typography>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((c) => (
                        <TableCell key={c.header} align={c.align || "left"} sx={{ color: "text.secondary", fontWeight: 700, whiteSpace: "nowrap" }}>{c.header}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((o) => (
                      <TableRow key={o.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpen(o)}>
                        {columns.map((c) => <TableCell key={c.header} align={c.align || "left"}>{c.render(o)}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <TablePagination
                component="div"
                count={filtered.length}
                page={pageSafe}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rpp}
                onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdersListTable;
